/**
 * Every word on the landing page, in both languages.
 *
 * Same shape as `docsContent.js`: one object per locale, read by the page and
 * handed down to the sections as a `t` prop. What stays in `content/site.js` is
 * the part that is not copy — accent colours, the per-feature gradient, store
 * URLs — so a translator never has to look at a hex value, and a colour tweak
 * never touches two locales.
 *
 * Product names are deliberately not translated; see the glossary note in
 * docs/client/landing.md.
 */

export const LANDING_LOCALES = ['en', 'vi'];

export const landingContent = {
  en: {
    meta: {
      title: 'Owla — Secure Team Messaging, Threads & AI Assistant',
    },
    hero: {
      eyebrow: 'ENTERPRISE MESSAGING PLATFORM',
      titleLead: 'Secure enterprise messaging built for ',
      titleAccent: 'modern teams.',
      sub: 'Private team communication with end-to-end encryption, role-based administration, directory management, and seamless integrations — all powered by Owla.',
      ctaDownload: 'Download free',
      ctaDemo: 'See it live',
      chatName: 'Pax · Product',
      chatStatus: 'Online · end-to-end encrypted',
      chatPlaceholder: 'Message Pax…',
      // The bubbles type in one-by-one on a loop.
      sequence: [
        { from: 'them', text: 'Can you ship the release notes today? 🚀' },
        { from: 'me', text: 'On it — drafting now.' },
        { from: 'ai', text: 'I read the thread: 3 action items, assigned to 2 people. Want a draft?' },
        { from: 'me', text: 'Perfect. Send it 💜' },
      ],
    },
    trust: {
      label: 'Secure messaging, built for modern teams.',
      stats: [
        { big: 'E2E', label: 'Encrypted by default' },
        { big: '100%', label: 'Data ownership' },
      ],
    },
    features: {
      eyebrow: 'EVERYTHING IN ONE PLACE',
      title: 'Keep business conversations secure and organized',
      lead: 'Most messaging tools were designed for mass communication. Owla is built for organizations that need control, privacy, and reliable collaboration — without sacrificing user experience.',
      items: [
        { title: 'End-to-end encryption', body: 'Every conversation is encrypted by default, ensuring only participants can access message content.' },
        { title: 'Direct & group chat', body: 'Send direct messages, create group conversations, share files, and collaborate instantly across teams.' },
        { title: 'Team directories', body: 'Organize employees into departments, business units, or client groups with controlled member visibility.' },
        { title: 'Message threads', body: 'Keep discussions focused by turning replies into threaded conversations without cluttering main channels.' },
        { title: 'Media & file sharing', body: 'Share documents, images, and business files securely within encrypted conversations.' },
        { title: 'AI assistant', body: 'Summarize threads, draft replies and turn chats into action items in a tap.' },
      ],
    },
    security: {
      eyebrow: 'ENTERPRISE-GRADE SECURITY',
      title: 'Enterprise-grade security by design',
      lead: 'Owla runs on the Matrix protocol, eliminating dependency on a single central messaging provider. Messages are encrypted before transmission and remain inaccessible to servers or unauthorized parties.',
      points: [
        'Decentralized architecture — no single point of failure',
        'Data ownership — your messages stay yours',
        'Self-hosted — deploy on your own infrastructure',
      ],
      shotAlt: 'Owla enterprise security and administration',
    },
    demo: {
      eyebrow: 'SEE IT IN ACTION',
      title: 'Try the assistant — right here',
      lead: 'Tap a prompt and watch Owla Assistant summarize, draft and organize the conversation, live.',
      assistant: 'Owla Assistant · product-owla',
      builtIn: 'Built into every conversation',
      chips: [
        { label: '📝 Summarize this thread', answer: 'Here’s the gist: the team aligned on shipping v2 Friday. Open items: finalize copy (Sarah), QA the Windows build (Pax), publish release notes (you).' },
        { label: '✅ Extract action items', answer: '3 action items created: 1) Finalize copy — Sarah. 2) QA Windows build — Pax. 3) Publish release notes — you. Synced to Google Tasks. ✓' },
        { label: '✍️ Draft a reply', answer: 'Draft: “Thanks all — release notes are in progress and will be live by EOD. I’ll tag everyone once published. 💜” Want me to send it?' },
      ],
    },
    ai: {
      eyebrow: 'BUILT-IN AI ASSISTANT',
      title: 'AI assistant for smarter conversations',
      lead: 'Optional AI tools help users summarize discussions, draft responses, improve writing, and identify action items directly within conversations.',
      points: [
        { title: 'Suggested replies', body: 'Smart reply suggestions and message rewriting in your tone.' },
        { title: 'Conversation summaries', body: 'Catch up on long discussions in seconds.' },
        { title: 'Action item extraction', body: 'Turn conversations into clear, assigned tasks.' },
        { title: 'Multi-provider support', body: 'Works with OpenAI, Claude and Gemini.' },
      ],
    },
    tasks: {
      eyebrow: 'TASKS & ACTIVITY',
      title: 'Turn chat into things that get done',
      lead: 'Owla integrates with business tools to streamline communication and automate routine processes — synchronizing calendar events and tasks across teams.',
      points: [
        'Connect with your existing workflows',
        'Google Workspace integration',
        'Webhooks for automation',
      ],
      shotAlt: 'Owla tasks',
    },
    useCases: {
      eyebrow: 'USE CASES',
      title: 'Enterprise messaging for secure team collaboration',
      lead: 'Owla is a secure enterprise messaging platform for organizations that require private communication, end-to-end encryption, directory management, and administrative control — helping businesses replace fragmented messaging tools with one unified, secure environment.',
      items: [
        { title: 'Internal team communication', body: 'Secure communication across departments and distributed teams.' },
        { title: 'Enterprise operations', body: 'Manage structured communication with administrative oversight.' },
        { title: 'Professional communities', body: 'Create private communication networks with controlled member access.' },
        { title: 'Education & training', body: 'Enable secure collaboration among instructors, students and administrators.' },
      ],
    },
    partners: {
      label: 'BUILT & BACKED BY',
    },
    download: {
      eyebrow: 'GET OWLA',
      title: 'One account. Every device.',
      lead: 'Download Owla and pick up the conversation wherever you are — desktop, laptop or phone.',
      note: 'Free for your team · Requires an organization invite to sign in',
      // Keyed by the platform ids in content/site.js.
      platforms: {
        macos: { meta: 'Apple Silicon & Intel', cta: 'Download' },
        windows: { meta: 'Windows 10 & 11', cta: 'Download' },
        ios: { meta: 'iPhone & iPad', cta: 'App Store' },
        android: { meta: 'Phone & tablet', cta: 'Google Play' },
      },
    },
    footer: {
      by: 'by Golden Owl Solutions',
      home: 'Home',
      docs: 'Document',
      support: 'Support',
      privacy: 'Privacy Policy',
    },
  },

  vi: {
    meta: {
      title: 'Owla — Nhắn tin bảo mật, luồng thảo luận & trợ lý AI',
    },
    hero: {
      eyebrow: 'NỀN TẢNG NHẮN TIN DOANH NGHIỆP',
      titleLead: 'Nhắn tin bảo mật cho ',
      titleAccent: 'đội ngũ hiện đại.',
      sub: 'Kênh trao đổi nội bộ riêng tư với mã hoá đầu cuối, phân quyền theo vai trò, quản lý danh bạ và tích hợp liền mạch — tất cả trên Owla.',
      ctaDownload: 'Tải miễn phí',
      ctaDemo: 'Xem thử ngay',
      chatName: 'Pax · Product',
      chatStatus: 'Đang hoạt động · mã hoá đầu cuối',
      chatPlaceholder: 'Nhắn cho Pax…',
      sequence: [
        { from: 'them', text: 'Hôm nay kịp ra release notes không? 🚀' },
        { from: 'me', text: 'Đang soạn đây.' },
        { from: 'ai', text: 'Mình đọc luồng rồi: 3 đầu việc, giao cho 2 người. Cần bản nháp không?' },
        { from: 'me', text: 'Chuẩn. Gửi đi nhé 💜' },
      ],
    },
    trust: {
      label: 'Nhắn tin bảo mật, dành cho đội ngũ hiện đại.',
      stats: [
        { big: 'E2E', label: 'Mã hoá mặc định' },
        { big: '100%', label: 'Toàn quyền dữ liệu' },
      ],
    },
    features: {
      eyebrow: 'TẤT CẢ TRONG MỘT',
      title: 'Giữ trao đổi công việc an toàn và ngăn nắp',
      lead: 'Phần lớn ứng dụng nhắn tin sinh ra cho số đông. Owla được xây cho tổ chức cần quyền kiểm soát, sự riêng tư và cộng tác đáng tin cậy — mà không phải đánh đổi trải nghiệm.',
      items: [
        { title: 'Mã hoá đầu cuối', body: 'Mọi cuộc trò chuyện được mã hoá mặc định, chỉ người trong cuộc đọc được nội dung.' },
        { title: 'Chat riêng & nhóm', body: 'Nhắn riêng, lập nhóm, chia sẻ tệp và cộng tác tức thì giữa các đội.' },
        { title: 'Danh bạ tổ chức', body: 'Sắp xếp nhân sự theo phòng ban, đơn vị kinh doanh hay nhóm khách hàng, kiểm soát ai nhìn thấy ai.' },
        { title: 'Luồng thảo luận', body: 'Đưa phần trả lời vào luồng riêng để bàn đúng việc mà không làm loãng kênh chính.' },
        { title: 'Chia sẻ tệp & hình ảnh', body: 'Gửi tài liệu, hình ảnh và tệp công việc an toàn ngay trong cuộc trò chuyện đã mã hoá.' },
        { title: 'Trợ lý AI', body: 'Tóm tắt luồng, soạn câu trả lời và biến cuộc trò chuyện thành đầu việc chỉ trong một chạm.' },
      ],
    },
    security: {
      eyebrow: 'BẢO MẬT CẤP DOANH NGHIỆP',
      title: 'Bảo mật cấp doanh nghiệp ngay từ thiết kế',
      lead: 'Owla chạy trên giao thức Matrix, không phụ thuộc vào một nhà cung cấp nhắn tin trung tâm nào. Tin nhắn được mã hoá trước khi rời khỏi máy bạn, máy chủ hay bên thứ ba đều không đọc được.',
      points: [
        'Kiến trúc phi tập trung — không có điểm chết đơn lẻ',
        'Sở hữu dữ liệu — tin nhắn của bạn là của bạn',
        'Tự vận hành — triển khai trên hạ tầng của chính bạn',
      ],
      shotAlt: 'Bảo mật và quản trị doanh nghiệp trên Owla',
    },
    demo: {
      eyebrow: 'XEM THỬ TẠI CHỖ',
      title: 'Thử trợ lý AI — ngay tại đây',
      lead: 'Chạm vào một gợi ý và xem Owla Assistant tóm tắt, soạn nháp, sắp xếp cuộc trò chuyện ngay trước mắt.',
      assistant: 'Owla Assistant · product-owla',
      builtIn: 'Có sẵn trong mọi cuộc trò chuyện',
      chips: [
        { label: '📝 Tóm tắt luồng này', answer: 'Tóm lại: cả nhóm chốt phát hành v2 vào thứ Sáu. Việc còn treo: chốt nội dung (Sarah), QA bản Windows (Pax), đăng release notes (bạn).' },
        { label: '✅ Trích đầu việc', answer: 'Đã tạo 3 đầu việc: 1) Chốt nội dung — Sarah. 2) QA bản Windows — Pax. 3) Đăng release notes — bạn. Đã đồng bộ sang Google Tasks. ✓' },
        { label: '✍️ Soạn giúp câu trả lời', answer: 'Bản nháp: “Cảm ơn cả nhà — release notes đang làm, cuối ngày sẽ lên. Đăng xong mình tag mọi người nhé. 💜” Gửi luôn không?' },
      ],
    },
    ai: {
      eyebrow: 'TRỢ LÝ AI TÍCH HỢP',
      title: 'Trợ lý AI cho những cuộc trò chuyện thông minh hơn',
      lead: 'Bộ công cụ AI tuỳ chọn giúp tóm tắt thảo luận, soạn câu trả lời, viết mượt hơn và nhận diện đầu việc ngay trong cuộc trò chuyện.',
      points: [
        { title: 'Gợi ý trả lời', body: 'Gợi ý câu trả lời và viết lại tin nhắn theo giọng văn của bạn.' },
        { title: 'Tóm tắt hội thoại', body: 'Bắt kịp một cuộc thảo luận dài chỉ trong vài giây.' },
        { title: 'Trích xuất đầu việc', body: 'Biến cuộc trò chuyện thành đầu việc rõ ràng, có người phụ trách.' },
        { title: 'Nhiều nhà cung cấp AI', body: 'Dùng được với OpenAI, Claude và Gemini.' },
      ],
    },
    tasks: {
      eyebrow: 'CÔNG VIỆC & HOẠT ĐỘNG',
      title: 'Biến cuộc trò chuyện thành việc chạy được',
      lead: 'Owla kết nối với các công cụ sẵn có để tinh gọn trao đổi và tự động hoá việc lặp lại — đồng bộ lịch và đầu việc cho cả đội.',
      points: [
        'Kết nối với quy trình sẵn có của bạn',
        'Tích hợp Google Workspace',
        'Webhook cho tự động hoá',
      ],
      shotAlt: 'Quản lý công việc trên Owla',
    },
    useCases: {
      eyebrow: 'TÌNH HUỐNG SỬ DỤNG',
      title: 'Nhắn tin doanh nghiệp cho cộng tác an toàn',
      lead: 'Owla là nền tảng nhắn tin doanh nghiệp dành cho tổ chức cần trao đổi riêng tư, mã hoá đầu cuối, quản lý danh bạ và quyền quản trị — giúp doanh nghiệp gom các công cụ nhắn tin rời rạc về một môi trường thống nhất và an toàn.',
      items: [
        { title: 'Trao đổi nội bộ', body: 'Kênh liên lạc an toàn giữa các phòng ban và đội ngũ làm việc phân tán.' },
        { title: 'Vận hành doanh nghiệp', body: 'Quản lý trao đổi có cấu trúc, kèm quyền giám sát của quản trị viên.' },
        { title: 'Cộng đồng chuyên môn', body: 'Xây mạng lưới trao đổi riêng tư, kiểm soát ai được tham gia.' },
        { title: 'Giáo dục & đào tạo', body: 'Cộng tác an toàn giữa giảng viên, học viên và ban quản lý.' },
      ],
    },
    partners: {
      label: 'XÂY DỰNG & BẢO TRỢ BỞI',
    },
    download: {
      eyebrow: 'TẢI OWLA',
      title: 'Một tài khoản. Mọi thiết bị.',
      lead: 'Tải Owla và tiếp tục cuộc trò chuyện ở bất cứ đâu — máy bàn, laptop hay điện thoại.',
      note: 'Miễn phí cho cả đội · Cần lời mời từ tổ chức để đăng nhập',
      platforms: {
        macos: { meta: 'Apple Silicon & Intel', cta: 'Tải về' },
        windows: { meta: 'Windows 10 & 11', cta: 'Tải về' },
        ios: { meta: 'iPhone & iPad', cta: 'App Store' },
        android: { meta: 'Điện thoại & máy tính bảng', cta: 'Google Play' },
      },
    },
    footer: {
      by: 'bởi Golden Owl Solutions',
      home: 'Trang chủ',
      docs: 'Hướng dẫn',
      support: 'Hỗ trợ',
      privacy: 'Chính sách bảo mật',
    },
  },
};
